import React, { useCallback, useEffect, useState } from 'react';
import {
  Box, Card, CardContent, Typography, Button, Table, TableBody, TableCell,
  TableHead, TableRow, IconButton, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, FormControl, InputLabel, Select, MenuItem, Chip,
  Alert, Stack, Tooltip, CircularProgress,
  Accordion, AccordionSummary, AccordionDetails, Stepper, Step, StepLabel,
  StepContent, Divider, Checkbox,
} from '@mui/material';
import {
  Add, Delete, ContentCopy, Refresh, Dns as DnsIcon,
  ExpandMore as ExpandMoreIcon, MenuBook as MenuBookIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { io } from 'socket.io-client';

const API_URL = process.env.REACT_APP_API_URL || window.location.origin;

const statusChip = (node) => {
  if (node.connected) return <Chip size="small" label="Online" sx={{ bgcolor: '#22c55e', color: '#fff' }} />;
  if (node.status === 'pending') return <Chip size="small" label="Pending join" sx={{ bgcolor: '#f59e0b', color: '#0a0c11' }} />;
  return <Chip size="small" label="Offline" sx={{ bgcolor: '#ef4444', color: '#fff' }} />;
};

function formatHeartbeat(value) {
  if (!value) return 'never';
  const seconds = Math.round((Date.now() - new Date(value).getTime()) / 1000);
  if (seconds < 5) return 'just now';
  if (seconds < 120) return `${seconds}s ago`;
  return `${Math.round(seconds / 60)}m ago`;
}

/** Monospace command/config block used in the setup guide. */
const Cmd = ({ children }) => (
  <Box
    component="pre"
    sx={{
      fontFamily: 'monospace', fontSize: 12.5, p: 1.5, borderRadius: 1,
      bgcolor: 'rgba(0,0,0,0.35)', whiteSpace: 'pre-wrap', wordBreak: 'break-all',
      userSelect: 'all', m: 0, my: 1,
    }}
  >
    {children}
  </Box>
);

/** Step-by-step guide for connecting a new worker VM. */
function SetupGuide({ masterOrigin }) {
  return (
    <Accordion disableGutters sx={{ '&:before': { display: 'none' } }}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <MenuBookIcon color="primary" fontSize="small" />
          <Typography variant="subtitle1">Setup guide: connecting a new worker VM</Typography>
        </Stack>
      </AccordionSummary>
      <AccordionDetails>
        <Stepper orientation="vertical" nonLinear sx={{ '& .MuiStepLabel-label': { fontWeight: 600 } }}>
          <Step active expanded>
            <StepLabel>Prepare the new Ubuntu VM</StepLabel>
            <StepContent>
              <Typography variant="body2" color="text.secondary">
                Use Ubuntu 22.04 or 24.04 with network access to this server. The worker runs
                the audio pipelines (FFmpeg, PulseAudio) for its assigned stations, so size CPU
                and RAM for the number of stations you plan to move to it. Copy the RadioSuite
                project folder onto the VM (git clone or scp) — the installer runs from there.
              </Typography>
            </StepContent>
          </Step>

          <Step active expanded>
            <StepLabel>Allow the worker to reach this server</StepLabel>
            <StepContent>
              <Typography variant="body2" color="text.secondary">
                The worker connects to this master's PostgreSQL (port 5432) and pushes its
                streams to this master's Icecast (port 8000). On <strong>this server</strong>, allow the
                worker's IP through the firewall:
              </Typography>
              <Cmd>{'sudo ufw allow from <WORKER_IP> to any port 5432\nsudo ufw allow from <WORKER_IP> to any port 8000'}</Cmd>
              <Typography variant="body2" color="text.secondary">
                Then let PostgreSQL accept remote connections. In{' '}
                <code>/etc/postgresql/*/main/postgresql.conf</code> set:
              </Typography>
              <Cmd>{"listen_addresses = '*'"}</Cmd>
              <Typography variant="body2" color="text.secondary">
                And add a line to <code>/etc/postgresql/*/main/pg_hba.conf</code>:
              </Typography>
              <Cmd>{'host  radiosuitex  radiosuitex  <WORKER_IP>/32  scram-sha-256'}</Cmd>
              <Typography variant="body2" color="text.secondary">
                Apply with <code>sudo systemctl restart postgresql</code>.
              </Typography>
            </StepContent>
          </Step>

          <Step active expanded>
            <StepLabel>Allow this server to reach the worker</StepLabel>
            <StepContent>
              <Typography variant="body2" color="text.secondary">
                Listener streams and studio contributions for remote stations are proxied through
                this master to the worker. On <strong>the worker VM</strong>, open:
              </Typography>
              <Cmd>{'sudo ufw allow from <MASTER_IP> to any port 8001   # direct listener streams\nsudo ufw allow from <MASTER_IP> to any port 8004   # WebRTC contribution ingest'}</Cmd>
            </StepContent>
          </Step>

          <Step active expanded>
            <StepLabel>Add the node here and run the join command</StepLabel>
            <StepContent>
              <Typography variant="body2" color="text.secondary">
                Click <strong>Add node</strong> below and give it a name. You'll get a one-time join
                command (valid 24 hours, single use) that looks like:
              </Typography>
              <Cmd>{`sudo bash install-radiosuitex.sh --worker --master=${masterOrigin} --token=<JOIN_TOKEN>`}</Cmd>
              <Typography variant="body2" color="text.secondary">
                Run it from the project folder on the new VM. It installs Node.js, FFmpeg and
                PulseAudio, exchanges the token for the node's credentials, writes the worker's
                configuration and starts the <code>radiosuitex-worker</code> service. No PostgreSQL,
                Icecast, nginx or frontend is installed on the worker.
              </Typography>
            </StepContent>
          </Step>

          <Step active expanded>
            <StepLabel>Verify and assign stations</StepLabel>
            <StepContent>
              <Typography variant="body2" color="text.secondary">
                Within a few seconds the node should show <strong>Online</strong> in the table below,
                with live CPU / memory / FFmpeg counts. Then use <strong>Station assignment</strong> to
                move stations onto it — tick several stations to move them in one go, or use the
                per-station dropdown. Each station moves together with all of its regional splits,
                and listeners, studios and this dashboard keep working unchanged. Expect a few
                seconds of silence on a station while it hands over.
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                A node can run stations, audio processing chains, or both - so you can also
                dedicate a VM purely to DSP processing by assigning only processing chains to it
                (see <strong>Audio processing assignment</strong> below).
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                To test the integration: assign a station to the node, confirm its stream keeps
                playing from the same URL, check now-playing updates on the dashboard, then move
                it back to the master.
              </Typography>
            </StepContent>
          </Step>
        </Stepper>

        <Divider sx={{ my: 2 }} />
        <Typography variant="caption" color="text.secondary">
          Troubleshooting: on the worker run <code>tail -f /var/log/radiosuitex/worker.log</code>.
          "Master connection error" means the worker cannot reach {masterOrigin}; database errors
          usually mean PostgreSQL access (step 2) is not set up; silent streams usually mean
          Icecast port 8000 is blocked. If a join token expires, use the refresh icon next to the
          pending node to generate a new command.
        </Typography>
      </AccordionDetails>
    </Accordion>
  );
}

/**
 * Server Nodes (superadmin only): optional multi-VM deployment for large
 * installs. Stations can be moved to worker VMs; end users see no difference.
 */
export default function ServerNodesSettings() {
  const [enabled, setEnabled] = useState(false);
  const [nodes, setNodes] = useState([]);
  const [stations, setStations] = useState([]);
  const [processing, setProcessing] = useState([]);
  const [procConfirm, setProcConfirm] = useState(null); // { instance, nodeId }
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [newNodeName, setNewNodeName] = useState('');
  const [joinInfo, setJoinInfo] = useState(null); // { name, joinToken }
  const [copied, setCopied] = useState(false);
  const [assignConfirm, setAssignConfirm] = useState(null); // { stations: [...], nodeId }
  const [selectedIds, setSelectedIds] = useState([]);       // bulk-move selection
  const [bulkTarget, setBulkTarget] = useState('');         // '' | 0 (master) | nodeId
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/server-nodes`, { withCredentials: true });
      setEnabled(res.data.enabled);
      setNodes(res.data.nodes || []);
      setStations(res.data.stations || []);
      setProcessing(res.data.processing || []);
      setError(null);
    } catch (e) {
      setError(e.response?.data?.message || e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Live updates when nodes connect/disconnect or stations move
  useEffect(() => {
    const socket = io(API_URL, { transports: ['websocket'] });
    socket.on('server-nodes:changed', load);
    const timer = setInterval(load, 15000);
    return () => { socket.disconnect(); clearInterval(timer); };
  }, [load]);

  const createNode = async () => {
    setError(null);
    setBusy(true);
    try {
      const res = await axios.post(`${API_URL}/api/server-nodes`, { name: newNodeName }, { withCredentials: true });
      setJoinInfo({ name: res.data.name, joinToken: res.data.joinToken });
      setNewNodeName('');
      setAddOpen(false);
      load();
    } catch (e) {
      setError(e.response?.data?.message || e.message);
    } finally {
      setBusy(false);
    }
  };

  const regenerateToken = async (node) => {
    setError(null);
    try {
      const res = await axios.post(`${API_URL}/api/server-nodes/${node.id}/regenerate-token`, {}, { withCredentials: true });
      setJoinInfo({ name: node.name, joinToken: res.data.joinToken });
    } catch (e) {
      setError(e.response?.data?.message || e.message);
    }
  };

  const removeNode = async (node) => {
    if (!window.confirm(`Delete node "${node.name}"?`)) return;
    setError(null);
    try {
      await axios.delete(`${API_URL}/api/server-nodes/${node.id}`, { withCredentials: true });
      load();
    } catch (e) {
      setError(e.response?.data?.message || e.message);
    }
  };

  const confirmAssign = async () => {
    if (!assignConfirm) return;
    setBusy(true);
    setError(null);
    const failures = [];
    // Sequential on purpose: each move is a live handover (stop on the old
    // owner, start on the new one) - parallel moves would thrash both nodes.
    for (const station of assignConfirm.stations) {
      try {
        await axios.put(
          `${API_URL}/api/server-nodes/assign/${station.id}`,
          { node_id: assignConfirm.nodeId },
          { withCredentials: true }
        );
      } catch (e) {
        failures.push(`${station.name}: ${e.response?.data?.message || e.message}`);
      }
    }
    if (failures.length > 0) setError(`Some stations could not be moved - ${failures.join('; ')}`);
    setAssignConfirm(null);
    setSelectedIds([]);
    setBulkTarget('');
    setBusy(false);
    load();
  };

  const confirmProcAssign = async () => {
    if (!procConfirm) return;
    setBusy(true);
    setError(null);
    try {
      await axios.put(
        `${API_URL}/api/server-nodes/assign-processing/${procConfirm.instance.id}`,
        { node_id: procConfirm.nodeId },
        { withCredentials: true }
      );
      setProcConfirm(null);
      load();
    } catch (e) {
      setError(e.response?.data?.message || e.message);
    } finally {
      setBusy(false);
    }
  };

  const toggleSelected = (stationId) => {
    setSelectedIds((prev) =>
      prev.includes(stationId) ? prev.filter((id) => id !== stationId) : [...prev, stationId]
    );
  };

  const startBulkAssign = () => {
    if (bulkTarget === '' || selectedIds.length === 0) return;
    const nodeId = bulkTarget === 0 ? null : bulkTarget;
    const toMove = stations.filter(
      (s) => selectedIds.includes(s.id) && (s.node_id || null) !== nodeId
    );
    if (toMove.length === 0) {
      setSelectedIds([]);
      setBulkTarget('');
      return; // everything selected already runs there
    }
    setAssignConfirm({ stations: toMove, nodeId });
  };

  const joinCommand = joinInfo
    ? `sudo bash install-radiosuitex.sh --worker --master=${window.location.origin} --token=${joinInfo.joinToken}`
    : '';

  const copyJoinCommand = () => {
    navigator.clipboard.writeText(joinCommand).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const nodeName = (nodeId) => nodes.find((n) => n.id === nodeId)?.name || `Node ${nodeId}`;

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>;
  }

  return (
    <Stack spacing={3}>
      {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}

      <Card>
        <CardContent>
          <Stack direction="row" alignItems="center" spacing={2}>
            <DnsIcon color="primary" />
            <Box sx={{ flexGrow: 1 }}>
              <Typography variant="h6">Multi-node deployment</Typography>
              <Typography variant="body2" color="text.secondary">
                For large installations: run stations' audio pipelines on separate worker VMs
                while keeping this single dashboard. Users and studios see no difference.
                Leave disabled for single-server installs.
              </Typography>
            </Box>
            <Chip
              label={enabled ? 'Enabled' : 'Disabled'}
              color={enabled ? 'success' : 'default'}
              title="Change this under Station and App Configuration → App Features"
            />
          </Stack>
        </CardContent>
      </Card>

      {enabled && (
        <>
          <SetupGuide masterOrigin={window.location.origin} />

          <Card>
            <CardContent>
              <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
                <Typography variant="h6">Worker nodes</Typography>
                <Button variant="contained" startIcon={<Add />} onClick={() => setAddOpen(true)}>
                  Add node
                </Button>
              </Stack>
              {nodes.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No worker nodes yet. Add a node to get a join command for the new VM.
                </Typography>
              ) : (
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Name</TableCell>
                      <TableCell>Host</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>CPU</TableCell>
                      <TableCell>Memory</TableCell>
                      <TableCell>FFmpeg</TableCell>
                      <TableCell>Heartbeat</TableCell>
                      <TableCell>Stations</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {nodes.map((node) => (
                      <TableRow key={node.id}>
                        <TableCell>{node.name}</TableCell>
                        <TableCell>{node.host || '—'}</TableCell>
                        <TableCell>{statusChip(node)}</TableCell>
                        <TableCell>{node.cpu_percent != null ? `${node.cpu_percent}%` : '—'}</TableCell>
                        <TableCell>{node.mem_percent != null ? `${node.mem_percent}%` : '—'}</TableCell>
                        <TableCell>{node.ffmpeg_count != null ? node.ffmpeg_count : '—'}</TableCell>
                        <TableCell>{formatHeartbeat(node.last_heartbeat)}</TableCell>
                        <TableCell>
                          {node.station_count > 0 ? (
                            <Tooltip title={(node.stations || []).map((s) => s.name).join(', ')}>
                              <Chip size="small" label={node.station_count} />
                            </Tooltip>
                          ) : 0}
                        </TableCell>
                        <TableCell align="right">
                          {node.status === 'pending' && (
                            <Tooltip title="Generate a new join command">
                              <IconButton size="small" onClick={() => regenerateToken(node)}>
                                <Refresh fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                          <Tooltip title={node.station_count > 0 ? 'Reassign stations first' : 'Delete node'}>
                            <span>
                              <IconButton
                                size="small"
                                onClick={() => removeNode(node)}
                                disabled={node.station_count > 0}
                              >
                                <Delete fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 1 }}>Station assignment</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                A station and all of its regional splits run together on the selected server.
                You can spread stations across servers however you like - e.g. four stations on
                one node and the rest on another. Moving a station causes a brief interruption
                while its streams restart.
              </Typography>

              {/* Bulk move toolbar */}
              <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
                <Typography variant="body2" color={selectedIds.length > 0 ? 'text.primary' : 'text.secondary'}>
                  {selectedIds.length > 0
                    ? `${selectedIds.length} station${selectedIds.length === 1 ? '' : 's'} selected`
                    : 'Select stations to move several at once'}
                </Typography>
                <FormControl size="small" sx={{ minWidth: 220 }} disabled={selectedIds.length === 0}>
                  <InputLabel id="bulk-target-label">Move selected to</InputLabel>
                  <Select
                    labelId="bulk-target-label"
                    label="Move selected to"
                    value={bulkTarget}
                    onChange={(e) => setBulkTarget(e.target.value)}
                  >
                    <MenuItem value={0}>Master (this server)</MenuItem>
                    {nodes.filter((n) => n.status !== 'pending').map((n) => (
                      <MenuItem key={n.id} value={n.id} disabled={!n.connected}>
                        {n.name}{!n.connected ? ' (offline)' : ''}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Button
                  variant="outlined"
                  onClick={startBulkAssign}
                  disabled={selectedIds.length === 0 || bulkTarget === '' || busy}
                >
                  Move
                </Button>
              </Stack>

              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">
                      <Checkbox
                        size="small"
                        checked={stations.length > 0 && selectedIds.length === stations.length}
                        indeterminate={selectedIds.length > 0 && selectedIds.length < stations.length}
                        onChange={(e) => setSelectedIds(e.target.checked ? stations.map((s) => s.id) : [])}
                      />
                    </TableCell>
                    <TableCell>Station</TableCell>
                    <TableCell>Runs on</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {stations.map((station) => (
                    <TableRow key={station.id} selected={selectedIds.includes(station.id)}>
                      <TableCell padding="checkbox">
                        <Checkbox
                          size="small"
                          checked={selectedIds.includes(station.id)}
                          onChange={() => toggleSelected(station.id)}
                        />
                      </TableCell>
                      <TableCell>{station.name}</TableCell>
                      <TableCell>
                        <FormControl size="small" sx={{ minWidth: 220 }}>
                          <Select
                            value={station.node_id || 0}
                            onChange={(e) => {
                              const nodeId = e.target.value === 0 ? null : e.target.value;
                              if ((station.node_id || null) === nodeId) return;
                              setAssignConfirm({ stations: [station], nodeId });
                            }}
                          >
                            <MenuItem value={0}>Master (this server)</MenuItem>
                            {nodes.filter((n) => n.status !== 'pending').map((n) => (
                              <MenuItem key={n.id} value={n.id} disabled={!n.connected}>
                                {n.name}{!n.connected ? ' (offline)' : ''}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          {processing.length > 0 && (
            <Card>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 1 }}>Audio processing assignment</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  DSP processing chains can run on a different server than the station itself -
                  they read the station's stream from this server's Icecast, so a node can be
                  dedicated purely to processing. Moving a chain briefly interrupts its processed
                  outputs (the raw station stream is unaffected).
                </Typography>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Processing chain</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Runs on</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {processing.map((inst) => (
                      <TableRow key={inst.id}>
                        <TableCell>
                          {inst.region_name
                            ? `${inst.station_name || inst.name} / ${inst.region_name}`
                            : (inst.station_name || inst.name)}
                          <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                            ({inst.preset})
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={inst.status === 'running' ? 'Running' : inst.status === 'error' ? 'Error' : 'Stopped'}
                            sx={{
                              bgcolor: inst.status === 'running' ? '#22c55e' : inst.status === 'error' ? '#ef4444' : 'rgba(255,255,255,0.12)',
                              color: 'var(--rs-text)',
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <FormControl size="small" sx={{ minWidth: 220 }}>
                            <Select
                              value={inst.node_id || 0}
                              onChange={(e) => {
                                const nodeId = e.target.value === 0 ? null : e.target.value;
                                if ((inst.node_id || null) === nodeId) return;
                                setProcConfirm({ instance: inst, nodeId });
                              }}
                            >
                              <MenuItem value={0}>Master (this server)</MenuItem>
                              {nodes.filter((n) => n.status !== 'pending').map((n) => (
                                <MenuItem key={n.id} value={n.id} disabled={!n.connected}>
                                  {n.name}{!n.connected ? ' (offline)' : ''}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}
        </>
      )}

      {/* Add node dialog */}
      <Dialog open={addOpen} onClose={() => setAddOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Add worker node</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label="Node name"
            placeholder="e.g. Audio Worker 1"
            value={newNodeName}
            onChange={(e) => setNewNodeName(e.target.value)}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={createNode} disabled={!newNodeName.trim() || busy}>
            Create
          </Button>
        </DialogActions>
      </Dialog>

      {/* One-time join command dialog */}
      <Dialog open={!!joinInfo} onClose={() => setJoinInfo(null)} maxWidth="md" fullWidth>
        <DialogTitle>Join command for "{joinInfo?.name}"</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            This command is shown once. Run it from the RadioSuite project folder on the new
            Ubuntu VM within 24 hours - the token is single-use and the node appears online
            automatically once it joins. Make sure the firewall and PostgreSQL steps from the
            setup guide are done first.
          </Alert>
          <Box
            sx={{
              fontFamily: 'monospace', fontSize: 13, p: 2, borderRadius: 1,
              bgcolor: 'rgba(0,0,0,0.35)', wordBreak: 'break-all', userSelect: 'all',
            }}
          >
            {joinCommand}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button startIcon={<ContentCopy />} onClick={copyJoinCommand}>
            {copied ? 'Copied!' : 'Copy'}
          </Button>
          <Button variant="contained" onClick={() => setJoinInfo(null)}>Done</Button>
        </DialogActions>
      </Dialog>

      {/* Assignment confirmation (single or bulk) */}
      <Dialog open={!!assignConfirm} onClose={() => !busy && setAssignConfirm(null)} maxWidth="xs" fullWidth>
        <DialogTitle>
          Move {assignConfirm?.stations?.length === 1 ? 'station' : `${assignConfirm?.stations?.length} stations`}?
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Move{' '}
            <strong>{(assignConfirm?.stations || []).map((s) => s.name).join(', ')}</strong> to{' '}
            <strong>{assignConfirm?.nodeId ? nodeName(assignConfirm.nodeId) : 'the master server'}</strong>?
          </Typography>
          <Alert severity="info" sx={{ mt: 2 }}>
            Each station's streams (including regional splits) will restart on the new
            server - expect a few seconds of silence per station during the handover.
            {(assignConfirm?.stations?.length || 0) > 1 && ' Stations are moved one at a time.'}
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAssignConfirm(null)} disabled={busy}>Cancel</Button>
          <Button
            variant="contained"
            color="warning"
            onClick={confirmAssign}
            disabled={busy}
            startIcon={busy ? <CircularProgress size={16} color="inherit" /> : null}
          >
            {busy ? 'Moving...' : (assignConfirm?.stations?.length === 1 ? 'Move station' : 'Move stations')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Processing assignment confirmation */}
      <Dialog open={!!procConfirm} onClose={() => !busy && setProcConfirm(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Move processing chain?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Move processing for{' '}
            <strong>
              {procConfirm?.instance?.region_name
                ? `${procConfirm?.instance?.station_name || procConfirm?.instance?.name} / ${procConfirm?.instance?.region_name}`
                : (procConfirm?.instance?.station_name || procConfirm?.instance?.name)}
            </strong>{' '}
            to <strong>{procConfirm?.nodeId ? nodeName(procConfirm.nodeId) : 'the master server'}</strong>?
          </Typography>
          <Alert severity="info" sx={{ mt: 2 }}>
            The processed outputs will drop for a few seconds while the chain restarts on the
            new server. The raw station stream is not affected.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setProcConfirm(null)} disabled={busy}>Cancel</Button>
          <Button variant="contained" color="warning" onClick={confirmProcAssign} disabled={busy}>
            {busy ? 'Moving...' : 'Move processing'}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
